import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { recomputeProveedorRatingAggregates } from "../utils/proveedores";
import { showAlert } from "../alertService";
import {
  StarIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/20/solid";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";

const ACCENT = "#1b4d2e";

// End-of-event performance review for a proveedor. Always "general" — there is
// no per-event flavor here. For per-event operational notes use the
// /comentarios subcollection from the event page.
const ProveedorReviewDialog = ({
  open,
  onClose,
  proveedorId,
  proveedorNombre,
  existing = null,
}) => {
  const { selectedVenueId } = useVenue();
  const { userInfo, user } = useAuth();

  const [rating, setRating] = useState(null);
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRating(existing?.rating ?? null);
    setComentario(existing?.comentario ?? "");
  }, [open, existing]);

  const isEditing = !!existing?.id;

  const canSubmit = !saving && comentario.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedVenueId || !proveedorId) return;
    setSaving(true);
    try {
      const payload = {
        rating: typeof rating === "number" ? rating : null,
        comentario: comentario.trim(),
        visibilidad: existing?.visibilidad || "privada",
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        const ref = doc(
          db,
          "venues",
          selectedVenueId,
          "proveedores",
          proveedorId,
          "reviews",
          existing.id
        );
        await updateDoc(ref, payload);
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.PROVEEDOR_REVIEW_EDIT,
            entity: "proveedor_review",
            entityId: existing.id,
            entityName: proveedorNombre || "",
            details: {
              proveedorId,
              proveedorNombre: proveedorNombre || "",
            },
          },
          userInfo,
          user
        );
      } else {
        const ref = collection(
          db,
          "venues",
          selectedVenueId,
          "proveedores",
          proveedorId,
          "reviews"
        );
        const docRef = await addDoc(ref, {
          ...payload,
          authorId: user?.uid || null,
          authorName: userInfo?.name || "",
          createdAt: serverTimestamp(),
        });
        logBitacora(
          selectedVenueId,
          {
            action: BITACORA_ACTIONS.PROVEEDOR_REVIEW_CREATE,
            entity: "proveedor_review",
            entityId: docRef.id,
            entityName: proveedorNombre || "",
            details: {
              proveedorId,
              proveedorNombre: proveedorNombre || "",
            },
          },
          userInfo,
          user
        );
      }

      recomputeProveedorRatingAggregates(selectedVenueId, proveedorId).catch(
        (e) => console.warn("recomputeProveedorRatingAggregates failed:", e)
      );

      showAlert(isEditing ? "Reseña actualizada" : "Reseña agregada", "success");
      onClose?.();
    } catch (e) {
      console.error(e);
      showAlert("Error al guardar reseña: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <ChatBubbleLeftRightIcon
                className="h-4 w-4"
                style={{ color: ACCENT }}
              />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                {isEditing ? "Editar reseña" : "Nueva reseña"}
              </h2>
              {proveedorNombre && (
                <div className="text-xs text-zinc-500 truncate">
                  {proveedorNombre}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <div className="text-xs text-zinc-500">
            Reseñas son evaluaciones del desempeño general del proveedor. Para
            notas operativas de un evento, usa los comentarios en la página del
            evento.
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-2">
              Calificación (opcional)
            </label>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = rating !== null && n <= rating;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n === rating ? null : n)}
                      className="p-0.5"
                      aria-label={`${n} estrellas`}
                    >
                      {filled ? (
                        <StarIcon className="h-7 w-7 text-amber-400" />
                      ) : (
                        <StarOutlineIcon className="h-7 w-7 text-zinc-300 hover:text-amber-300" />
                      )}
                    </button>
                  );
                })}
              </div>
              {rating !== null && (
                <button
                  type="button"
                  onClick={() => setRating(null)}
                  className="text-xs text-zinc-500 hover:text-zinc-700"
                >
                  Quitar
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">
              Comentario <span className="text-red-500">*</span>
            </label>
            <textarea
              autoFocus
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={5}
              placeholder="¿Qué tal trabajaron en general? ¿Algo a destacar o evitar?"
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>

          <div className="text-xs text-zinc-400">
            Esta reseña es privada del venue. En el futuro podrás marcarla como
            compartible con la comunidad.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {saving
              ? "Guardando..."
              : isEditing
              ? "Guardar cambios"
              : "Agregar reseña"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProveedorReviewDialog;
